/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";

function SearchableSelect({
  title,
  name,
  value,
  options,
  placeholder = "Select an option",
  required = false,
  error,
  handleChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const searchInputRef = useRef(null);

  const t = useTranslations("General");

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery === "") {
      setFilteredOptions(options);
    } else {
      const filtered = Array.isArray(options)
        ? options.filter((option) =>
            (option.name || option.label || "")
              .toLowerCase()
              .includes(searchQuery.toLowerCase()),
          )
        : Object.entries(options).filter(([key, label]) =>
            label.toLowerCase().includes(searchQuery.toLowerCase()),
          );
      setFilteredOptions(filtered);
    }
  }, [searchQuery, options]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".relative")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleOptionSelect = (selectedValue) => {
    handleChange({ target: { name, value: selectedValue } });
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <label htmlFor={name} className="block mb-2 text-sm font-medium text-gray-900">
        {title}{required ? '*' : ''}
      </label>
      <div
        className="relative bg-gray-50 border border-gray-300 text-gray-900 rounded-lg transition-all focus:ring-primary-500 focus:border-primary-500 block w-full p-2"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <button type="button" className="w-full text-left rtl:text-right" data-testid={`${name}-dropdown-button`}>
          {value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </button>
        <span className="absolute inset-y-0 right-2 rtl:left-2 rtl:right-auto flex items-center pointer-events-none">
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </div>
      {error && <p className="text-red-500 text-sm" role="alert" aria-live="polite">{error}</p>}
      {isOpen && (
        <div
          className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={searchInputRef}
            type="text"
            className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg transition-all focus:ring-primary-500 focus:border-primary-500 block w-full p-2"
            placeholder={t("search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid={`${name}-search-input`}
          />
          <ul className="max-h-60 overflow-auto p-2">
            {Array.isArray(filteredOptions) && filteredOptions.length > 0
              ? filteredOptions.map((option) => (
                  <li
                    key={option.id || option.value || option.name || option.label}
                    className="p-2 hover:bg-gray-100 cursor-pointer rounded-md"
                    onClick={() => handleOptionSelect(option.name || option.id)}
                    data-testid={`${name}-option-${option.name || option.id}`}
                  >
                    {option.name || option.label}
                  </li>
                ))
              : Object.entries(filteredOptions).length > 0
              ? Object.entries(filteredOptions).map(([key, label]) => (
                  <li
                    key={key}
                    className="p-2 hover:bg-gray-100 cursor-pointer rounded-md"
                    onClick={() => handleOptionSelect(key)}
                    data-testid={`${name}-option-${key}`}
                  >
                    {label}
                  </li>
                ))
              : <li className="p-2 text-gray-500">{t("no_options")}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
